import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { day } from "../format";
import type { ImportBatch, Tradebook } from "../types";

/**
 * Every import, with an undo.
 *
 * Sending a file to the wrong account is the one import mistake that is both
 * easy to make and invisible afterwards — the rows look identical to any other
 * import. Recording each upload as a batch is what makes it reversible: move it
 * to the right book, or remove it entirely.
 *
 * Notes survive either way. They attach by match_key, which a moved execution
 * keeps, and are never deleted — so re-importing a removed file restores them.
 */
export function ImportBatches({
  books,
  activeBook,
  onChanged,
}: {
  books: Tradebook[];
  activeBook: Tradebook | null;
  onChanged: () => void;
}) {
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBatches(await api.batches());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const move = async (batch: ImportBatch, tradebookId: number) => {
    setBusy(batch.id);
    setError(null);
    setMessage(null);
    try {
      await api.moveBatch(batch.id, tradebookId);
      const target = books.find((b) => b.id === tradebookId);
      setMessage(
        `Moved ${batch.live_rows} fills to ${target?.name}. Both books were rebuilt.`,
      );
      await load();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (batch: ImportBatch) => {
    if (
      !confirm(
        `Delete this import? ${batch.live_rows} broker fills will be removed from ` +
          `${batch.tradebook} and its trades rebuilt.\n\n` +
          `Your notes are keyed separately and are not deleted — re-importing the ` +
          `same file restores them.`,
      )
    ) {
      return;
    }
    setBusy(batch.id);
    setError(null);
    setMessage(null);
    try {
      const r = await api.deleteBatch(batch.id);
      setMessage(`Removed ${r.deleted_executions} fills and rebuilt ${batch.tradebook}.`);
      await load();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card">
      <div className="section-title">Import history</div>
      <p className="hint" style={{ marginTop: 0 }}>
        Each upload is recorded so it can be undone. If a file landed in the wrong account,
        move it here rather than re-importing — moving rebuilds both books and keeps every
        note attached.
      </p>

      {error && <div className="banner error">{error}</div>}
      {message && <div className="banner ok">{message}</div>}

      {batches.length === 0 ? (
        <div className="hint">Nothing imported yet.</div>
      ) : (
        <table className="legs">
          <thead>
            <tr>
              <th>Imported</th>
              <th>File</th>
              <th>Account</th>
              <th>Book</th>
              <th>Fills</th>
              <th>Trade dates</th>
              <th>Move to</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => {
              const mismatch =
                batch.detected_client_id &&
                books.find((b) => b.id === batch.tradebook_id)?.client_id &&
                books.find((b) => b.id === batch.tradebook_id)?.client_id !==
                  batch.detected_client_id;
              return (
                <tr key={batch.id}>
                  <td className="nowrap">{batch.imported_at.slice(0, 16).replace("T", " ")}</td>
                  <td title={batch.filename ?? ""}>
                    {(batch.filename ?? "—").slice(0, 34)}
                  </td>
                  <td>
                    {batch.detected_client_id ?? "—"}
                    {mismatch && (
                      <span className="badge muted" title="File account differs from the book's">
                        mismatch
                      </span>
                    )}
                  </td>
                  <td>
                    <strong>{batch.tradebook}</strong>
                  </td>
                  <td>
                    {batch.live_rows}
                    {batch.live_rows !== batch.row_count && (
                      <span className="muted"> of {batch.row_count}</span>
                    )}
                  </td>
                  <td className="nowrap muted">
                    {batch.first_trade_date ? day(batch.first_trade_date) : "—"} →{" "}
                    {batch.last_trade_date ? day(batch.last_trade_date) : "—"}
                  </td>
                  <td>
                    <select
                      value=""
                      disabled={busy === batch.id}
                      onChange={(e) => e.target.value && move(batch, Number(e.target.value))}
                    >
                      <option value="">Move…</option>
                      {books
                        .filter((b) => b.id !== batch.tradebook_id)
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className="ghost danger"
                      disabled={busy === batch.id}
                      onClick={() => remove(batch)}
                      title="Undo this import"
                    >
                      {busy === batch.id ? "…" : "🗑"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {activeBook && !activeBook.client_id && (
        <div className="banner warn" style={{ marginTop: 12 }}>
          <strong>{activeBook.name} has no broker account code.</strong> The wrong-account
          check cannot run without it, which is how a file can land in the wrong book
          unnoticed. Set it in the table above.
        </div>
      )}
    </div>
  );
}
